import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useParams } from "react-router-dom";
import { Provider as ReduxProvider } from 'react-redux';
import { store } from './store';
import { RFQProvider, useRFQ } from "./contexts/RFQContext";
import { AuthProvider } from "./contexts/AuthContext";
import { FeedbackProvider, useFeedback } from "./contexts/FeedbackContext";
import Dashboard from "./pages/Dashboard";
import RFQWizard from "./pages/wizard/RFQWizardRoute";
import Login from "./pages/Login";
import AuthAction from "./pages/AuthAction";
import RFQList from "./pages/RFQList";
import DesignSystem from "./pages/DesignSystem";
import PrivateRoute from "./components/common/PrivateRoute";
import NewRFQModal from "./components/common/NewRFQModal";
import HandoffNoticeStack from "./components/common/HandoffNoticeStack";
import { ConnectedFeedbackPanel } from "./components/feedback";
import { useEffect, useState } from "react";
import voiceAppCommandBus from "./services/VoiceAppCommandBus";
import { ENV } from "./utils/env";
import { WIZARD_STEP_ROUTES, rfqPath, wizardStepPath } from "./utils/wizardRoutes";
import type { RFQEndUser } from "./types/rfq.types";
import './App.css';
import { displayRfqId } from "./utils/rfqIdentity";

// No vendor AI keys in the browser: any voice/LLM feature goes through our
// backend, so the app only needs its API base URL.
const API_URL = ENV.API_URL();
if (typeof API_URL !== "string" || !API_URL) {
  throw new Error("set REACT_APP_API_URL in environment variables");
}

// Old bookmarks keep working: the mechanic-named URL forwards to the
// resource URL, which resolves to the RFQ's current step.
const LegacyWizardRedirect: React.FC = () => {
  const { rfqId } = useParams<{ rfqId: string }>();
  return <Navigate to={rfqId ? rfqPath(rfqId) : "/dashboard"} replace />;
};

/**
 * `hellorobbie.com/feedback` — the link in the invitation email. It is a way
 * IN, not a page: it opens Robbie's panel over the dashboard and clears itself
 * out of the URL, so Back does not walk the person through an empty route and
 * a refresh does not reopen the panel they just closed.
 */
const FeedbackDeepLink: React.FC = () => {
  const { openFeedback } = useFeedback();
  useEffect(() => { openFeedback("link"); }, [openFeedback]);
  return <Navigate to="/dashboard" replace />;
};

function App() {
  return (
    <ReduxProvider store={store}>
      <AuthProvider>
        <RFQProvider>
          <Router>
            <FeedbackProvider>
              <AppContent />
            </FeedbackProvider>
          </Router>
        </RFQProvider>
      </AuthProvider>
    </ReduxProvider>
  );
}

// Component that has access to navigation inside Router context
const AppContent: React.FC = () => {
  const navigate = useNavigate();

  const { createRFQ } = useRFQ();

  // "New RFQ" opens the naming popup first — nothing is created until the
  // name is confirmed there (Cancel leaves no orphan RFQ docs). Navigation
  // itself never creates records: there is no /rfqs/new route.
  const [namingOpen, setNamingOpen] = useState(false);

  const handleCreateRFQ = () => setNamingOpen(true);

  const handleConfirmCreateRFQ = async (name: string, endUser?: RFQEndUser) => {
    const newRFQ = await createRFQ(name, endUser);
    setNamingOpen(false);
    navigate(wizardStepPath(newRFQ.rfqId, { step: 1, subStep: 1 }));
  };

  const handleViewRFQ = (rfqId: string) => {
    navigate(rfqPath(rfqId));
  };

  // Register app commands with voice command bus
  useEffect(() => {
    voiceAppCommandBus.registerAppCommand('createRFQ', async () => {
      try {
        const newRFQ = await createRFQ();
        navigate(wizardStepPath(newRFQ.rfqId, { step: 1, subStep: 1 }));

        voiceAppCommandBus.sendVoiceFeedback('rfqCreated', {
          rfq: newRFQ,
          message: `RFQ ${displayRfqId(newRFQ.rfqId)} created successfully`
        });

        return {
          success: true,
          message: 'RFQ created and navigated to wizard',
          data: newRFQ
        };
      } catch (error) {
        return {
          success: false,
          message: 'Failed to create RFQ',
          data: error
        };
      }
    });

    voiceAppCommandBus.registerAppCommand('navigateTo', async ({ destination }) => {
      const routeMap: Record<string, string> = {
        'dashboard': '/dashboard',
        'home': '/dashboard'
      };

      const route = routeMap[destination] || destination;
      navigate(route);

      voiceAppCommandBus.sendVoiceFeedback('navigationCompleted', {
        destination,
        route
      });

      return {
        success: true,
        message: `Navigated to ${destination}`,
        data: { destination, route }
      };
    });
  }, [navigate, createRFQ]);

  return (
    <>
    <NewRFQModal open={namingOpen} onClose={() => setNamingOpen(false)} onCreate={handleConfirmCreateRFQ} />
    {/* App-wide handoff toasts (polled while the tab is visible) — idle until signed in. */}
    <HandoffNoticeStack />
    {/* The one feedback panel, floating over whatever page is showing — the
        wizard included, which has its own chrome and no top bar of its own. */}
    <ConnectedFeedbackPanel />
    <Routes>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />

      {/* Login Page */}
      <Route path="/login" element={<Login />} />

      {/* Emailed account links (password reset) land here — public. */}
      <Route path="/auth/action" element={<AuthAction />} />

      {/* v2 primitives gallery (dev reference, anonymous by design) */}
      <Route path="/design" element={<DesignSystem />} />


      <Route path="/dashboard" element={
        <PrivateRoute>
          <Dashboard onCreateRFQ={handleCreateRFQ} onViewRFQ={handleViewRFQ} />
        </PrivateRoute>
      } />

      {/* RFQs are resources: /rfqs is the org-wide collection page, then
          /rfqs/:rfqId/<step> with the human RFQ number in the path. The bare
          RFQ URL resolves to its current step. */}
      <Route path="/rfqs" element={
        <PrivateRoute>
          <RFQList onCreateRFQ={handleCreateRFQ} onViewRFQ={handleViewRFQ} />
        </PrivateRoute>
      } />
      <Route path="/rfqs/:rfqId" element={
        <PrivateRoute>
          <RFQWizard />
        </PrivateRoute>
      } />
      {WIZARD_STEP_ROUTES.map(({ path, pos }) => (
        <Route key={path} path={`/rfqs/:rfqId/${path}`} element={
          <PrivateRoute>
            <RFQWizard routePos={pos} />
          </PrivateRoute>
        } />
      ))}
      <Route path="/rfq-wizard/:rfqId" element={<LegacyWizardRedirect />} />

      {/* Deep link from the invitation email — opens the panel, keeps no route. */}
      <Route path="/feedback" element={
        <PrivateRoute>
          <FeedbackDeepLink />
        </PrivateRoute>
      } />

      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
    </>
  );
};

export default App;
